<template>
  <div class="pp-body">
    <!-- ─── HERO ─── -->
    <div class="hero">
      <!-- Default pattern background (always rendered behind) -->
      <div class="hero-default-bg" />

      <!-- Actual cover image (only when user has one) -->
      <Transition name="hero-fade">
        <v-img
          v-if="coverImage"
          :src="coverImage"
          class="hero-photo"
          cover
        >
          <template #placeholder>
            <div class="hero-skeleton">
              <v-skeleton-loader type="image" class="fill-height" />
            </div>
          </template>
        </v-img>
      </Transition>

      <div class="hero-grad"></div>

      <!-- Name + badges — bottom of hero -->
      <div class="hero-identity">
        <div class="hero-name">{{ fullName }}</div>
        <div class="hero-badges">
          <div v-if="teamLogo" class="badge badge-team" @click="$emit('goToTeam')">
            <img :src="teamLogo" alt="" />
            {{ teamName }}
          </div>
          <div v-if="user?.no" class="badge badge-jersey">#{{ user.no }}</div>
          <div v-if="seasonView.primary" class="badge badge-role">
            {{ seasonView.haveBoth ? $t('player/goalie') : $t(seasonView.primary.role) }}
          </div>
          <div v-if="user?.rating" class="badge badge-rating">★ {{ user.rating }}</div>
          <div v-if="showShareBadge" class="badge badge-share" @click="$emit('share')">
            <v-icon size="16">mdi-share-variant-outline</v-icon>
          </div>
        </div>
      </div>
    </div>

    <!-- ─── INFO BAR ─── -->
    <div class="hero-info-bar">
      <div class="avatar-wrap">
        <v-avatar size="88" :style="{ background: getAvatarColor(user?.firstName || '', user?.lastName || '') }" class="avatar">
          <template v-if="avatarImage">
            <v-img :src="avatarImage" cover>
              <template #error>
                <div class="avatar-fallback">{{ initials }}</div>
              </template>
            </v-img>
          </template>
          <template v-else>
            <div class="avatar-fallback">{{ initials }}</div>
          </template>
        </v-avatar>
      </div>

      <div class="bio-facts bio-facts--compact">
        <!-- <div class="fact">
          <div class="fact-label">{{ $t('height') }}</div>
          <div class="fact-value" :class="{ empty: !user?.profile?.height }">{{ user?.profile?.height || '—' }}</div>
        </div>
        <div class="fact">
          <div class="fact-label">{{ $t('weight') }}</div>
          <div class="fact-value" :class="{ empty: !user?.profile?.weight }">{{ user?.profile?.weight || '—' }}</div>
        </div> -->
        <div class="fact">
          <div class="fact-label">{{ $t('born') }}</div>
          <div class="fact-value" :class="{ empty: !user?.profile?.born }">{{ user?.profile?.born || '—' }}</div>
        </div>
        <div class="fact">
          <div class="fact-label">{{ $t('city') }}</div>
          <div class="fact-value" :class="{ empty: !user?.profile?.city }">{{ user?.profile?.city || '—' }}</div>
        </div>
      </div>
    </div>

    <!-- ─── DETAIL FACTS (email, phone, jersey) ─── -->
    <div v-if="user?.email || user?.phone || user?.no" class="bio-facts bio-facts--detail">
      <div v-if="user?.email" class="fact fact--wide">
        <div class="fact-label">{{ $t('email') }}</div>
        <div class="fact-value fact-value--truncate" style="display: flex; align-items: center; gap: 8px;">
          <span>{{ user.email }}</span>
          <v-icon size="18" class="copy-icon" style="cursor:pointer;" @click="copyToClipboard(user.email, 'email')">mdi-content-copy</v-icon>
        </div>
      </div>
      <div v-if="user?.phone" class="fact">
        <div class="fact-label">{{ $t('phone') }}</div>
        <div class="fact-value" style="display: flex; align-items: center; gap: 8px;">
          <span style="cursor: pointer; text-decoration: underline;" @click="dialPhone(user.phone)">{{ user.phone }}</span>
          <v-icon size="18" class="copy-icon" style="cursor:pointer;" @click.stop="copyToClipboard(user.phone, 'phone')">mdi-content-copy</v-icon>
        </div>
      </div>
      <div v-if="user?.no" class="fact">
        <div class="fact-label">{{ $t('jerseyNumber') }}</div>
        <div class="fact-value">#{{ user.no }}</div>
      </div>
    </div>

    <v-snackbar v-model="showCopySnackbar" location="top" color="black" timeout="3000" :z-index="10000">
      <v-icon>mdi-content-copy</v-icon> {{ copySnackbarText }}
    </v-snackbar>

    <!-- ─── TABLES ─── -->
    <div v-if="showTables" class="tables-section">

      <!-- Stats -->
      <div v-if="groupedSeasonStats.length || seasonView.primary" class="ios-section">
        <div class="ios-section-header">
          <span class="ios-section-title">{{ $t('stats') }}</span>
        </div>
        <div class="section-content">
          <div class="hb-table-wrap">
            <div class="hb-table-scroll">
              <table class="hb-table">
                <thead>
                  <tr>
                    <th>{{ $t('season') }}</th>
                    <th class="hb-stat-header">{{ $t('GP') }}</th>
                    <th class="hb-stat-header">{{ labelFor(statsHeaderRole, 'c3') }}</th>
                    <th class="hb-stat-header">{{ labelFor(statsHeaderRole, 'c4') }}</th>
                    <th class="hb-stat-header">{{ labelFor(statsHeaderRole, 'c5') }}</th>
                    <th class="hb-stat-header">{{ labelFor(statsHeaderRole, 'c6') }}</th>
                  </tr>
                </thead>
                <tbody>
                  <template v-for="group in groupedSeasonStats" :key="group.key">
                    <tr
                      :class="{ 'expandable-row': group.hasMultiplePhases, 'expanded-row': expandedSeasons.has(group.key) }"
                      @click="group.hasMultiplePhases && toggleSeason(group.key)"
                    >
                      <td class="td-season">
                        <div class="season-cell">
                          <v-icon v-if="group.hasMultiplePhases" size="14" class="expand-icon" :class="{ 'expand-icon--open': expandedSeasons.has(group.key) }">mdi-chevron-right</v-icon>
                          <img v-if="group.leagueLogo" :src="getLeagueImage(group.leagueLogo)" alt="" class="league-logo" />
                          <span>{{ group.seasonYear }}</span>
                        </div>
                      </td>
                      <td class="hb-stat" :class="{ zero: !group.totalView.primary.data.gp }">{{ group.totalView.primary.data.gp ?? '-' }}</td>
                      <td class="hb-stat" :class="{ zero: !group.totalView.primary.data[keyFor(group.totalView.primary.role, 'c3')] }">{{ group.totalView.primary.data[keyFor(group.totalView.primary.role, 'c3')] ?? '-' }}</td>
                      <td class="hb-stat" :class="{ zero: !group.totalView.primary.data[keyFor(group.totalView.primary.role, 'c4')] }">{{ group.totalView.primary.data[keyFor(group.totalView.primary.role, 'c4')] ?? '-' }}</td>
                      <td class="hb-stat ice">{{ valueWithFormat(group.totalView.primary.role, 'c5', group.totalView.primary.data) }}</td>
                      <td class="hb-stat" :class="{ zero: !group.totalView.primary.data[keyFor(group.totalView.primary.role, 'c6')] }">{{ valueWithFormat(group.totalView.primary.role, 'c6', group.totalView.primary.data) }}</td>
                    </tr>
                    <template v-if="expandedSeasons.has(group.key)">
                      <tr v-for="ph in group.phases" :key="ph.phase" class="phase-row">
                        <td class="td-season td-phase">
                          <span class="phase-label">{{ phaseLabel(ph.phase) }}</span>
                        </td>
                        <td class="hb-stat" :class="{ zero: !ph.view.primary.data.gp }">{{ ph.view.primary.data.gp ?? '-' }}</td>
                        <td class="hb-stat" :class="{ zero: !ph.view.primary.data[keyFor(ph.view.primary.role, 'c3')] }">{{ ph.view.primary.data[keyFor(ph.view.primary.role, 'c3')] ?? '-' }}</td>
                        <td class="hb-stat" :class="{ zero: !ph.view.primary.data[keyFor(ph.view.primary.role, 'c4')] }">{{ ph.view.primary.data[keyFor(ph.view.primary.role, 'c4')] ?? '-' }}</td>
                        <td class="hb-stat ice">{{ valueWithFormat(ph.view.primary.role, 'c5', ph.view.primary.data) }}</td>
                        <td class="hb-stat" :class="{ zero: !ph.view.primary.data[keyFor(ph.view.primary.role, 'c6')] }">{{ valueWithFormat(ph.view.primary.role, 'c6', ph.view.primary.data) }}</td>
                      </tr>
                    </template>
                  </template>
                  <tr v-if="careerView.primary" class="career-row">
                    <td class="td-season">{{ $t('career') }}</td>
                    <td class="hb-stat" :class="{ zero: !careerView.primary!.data.gp }">{{ careerView.primary!.data.gp ?? '-' }}</td>
                    <td class="hb-stat" :class="{ zero: !careerView.primary!.data[keyFor(careerView.primary!.role, 'c3')] }">{{ careerView.primary!.data[keyFor(careerView.primary!.role, 'c3')] ?? '-' }}</td>
                    <td class="hb-stat" :class="{ zero: !careerView.primary!.data[keyFor(careerView.primary!.role, 'c4')] }">{{ careerView.primary!.data[keyFor(careerView.primary!.role, 'c4')] ?? '-' }}</td>
                    <td class="hb-stat ice">{{ valueWithFormat(careerView.primary!.role, 'c5', careerView.primary!.data) }}</td>
                    <td class="hb-stat" :class="{ zero: !careerView.primary!.data[keyFor(careerView.primary!.role, 'c6')] }">{{ valueWithFormat(careerView.primary!.role, 'c6', careerView.primary!.data) }}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>

      <!-- Recent games -->
      <div v-if="user?._id" class="ios-section">
        <div class="ios-section-header">
          <span class="ios-section-title">{{ $t('matchList') }} <span v-if="matchesTableRef?.total" class="hb-table-count">({{ matchesTableRef.total }})</span></span>
        </div>
        <div class="section-content">
          <MatchesTable ref="matchesTableRef" :user-id="user._id" :role="seasonView.primary?.role === 'goalie' ? 'g' : 'p'"
            :league-id="user._teams?.[0]?.leagueId ?? null" embedded />
        </div>
        <div v-if="matchesTableRef?.rows?.length && matchesTableRef.rows.length < (matchesTableRef.total ?? 0)" style="padding: 0 16px;">
          <button class="load-more-btn" :disabled="matchesTableRef?.loadingMore" @click="matchesTableRef?.loadMore()">
            <v-progress-circular v-if="matchesTableRef?.loadingMore" indeterminate size="16" width="2" />
            <template v-else>{{ $t('loadMore') }} <span class="load-more-count">({{ (matchesTableRef?.total ?? 0) - (matchesTableRef?.rows?.length ?? 0) }})</span></template>
          </button>
        </div>
      </div>

      <!-- Training history -->
      <div v-if="user?._id" class="ios-section">
        <div class="ios-section-header">
          <span class="ios-section-title">{{ $t('trainingHistory') }} <span v-if="trainingsTableRef?.total" class="hb-table-count">({{ trainingsTableRef.total }})</span></span>
        </div>
        <div class="section-content">
          <TrainingsTable ref="trainingsTableRef" :user-id="user._id" embedded />
        </div>
        <div v-if="trainingsTableRef?.rows?.length && trainingsTableRef.rows.length < (trainingsTableRef.total ?? 0)" style="padding: 0 16px;">
          <button class="load-more-btn" :disabled="trainingsTableRef?.loadingMore" @click="trainingsTableRef?.loadMore()">
            <v-progress-circular v-if="trainingsTableRef?.loadingMore" indeterminate size="16" width="2" />
            <template v-else>{{ $t('loadMore') }} <span class="load-more-count">({{ (trainingsTableRef?.total ?? 0) - (trainingsTableRef?.rows?.length ?? 0) }})</span></template>
          </button>
        </div>
      </div>

      <!-- No-show history -->
      <div v-if="user?._id" v-show="noShowTableRef?.total" class="ios-section">
        <div class="ios-section-header">
          <span class="ios-section-title">
            <v-icon size="18" color="warning" class="mr-1" style="margin-top: -2px;">mdi-alert</v-icon>
            {{ $t('noShowTitle') }} <span v-if="noShowTableRef?.total" class="hb-table-count">({{ noShowTableRef.total }})</span>
          </span>
        </div>
        <div class="section-content">
          <NoShowTable ref="noShowTableRef" :user-id="user._id" embedded />
        </div>
        <div v-if="noShowTableRef?.rows?.length && noShowTableRef.rows.length < (noShowTableRef.total ?? 0)" style="padding: 0 16px;">
          <button class="load-more-btn" :disabled="noShowTableRef?.loadingMore" @click="noShowTableRef?.loadMore()">
            <v-progress-circular v-if="noShowTableRef?.loadingMore" indeterminate size="16" width="2" />
            <template v-else>{{ $t('loadMore') }} <span class="load-more-count">({{ (noShowTableRef?.total ?? 0) - (noShowTableRef?.rows?.length ?? 0) }})</span></template>
          </button>
        </div>
      </div>

    </div>

  </div>
</template>

<script setup lang="ts">
import { computed, ref, onMounted, nextTick } from 'vue'
// Copy to clipboard snackbar logic
const showCopySnackbar = ref(false)
const copySnackbarText = ref('')

function copyWithFallback(value: string): boolean {
  const textarea = document.createElement('textarea')
  textarea.value = value
  textarea.setAttribute('readonly', '')
  textarea.style.position = 'fixed'
  textarea.style.opacity = '0'
  document.body.appendChild(textarea)
  textarea.select()

  let ok = false
  try {
    ok = document.execCommand('copy')
  } finally {
    document.body.removeChild(textarea)
  }

  return ok
}

async function copyToClipboard(value: string, type: 'email' | 'phone') {
  if (!value) return

  const label = type === 'email' ? 'E-mail' : 'Telefon'
  let copied = false

  try {
    if (navigator?.clipboard?.writeText) {
      await navigator.clipboard.writeText(value)
      copied = true
    } else {
      copied = copyWithFallback(value)
    }
  } catch {
    copied = copyWithFallback(value)
  }

  copySnackbarText.value = copied
    ? `Zkopirovano do schranky (${label})`
    : `Nepodarilo se zkopirovat do schranky (${label})`
  showCopySnackbar.value = false
  await nextTick()
  showCopySnackbar.value = true
}

function dialPhone(phone: string) {
  if (!phone) return

  const sanitizedPhone = String(phone).trim().replace(/[^\d+]/g, '')
  if (!sanitizedPhone) return

  window.location.href = `tel:${sanitizedPhone}`
}
import { useI18n } from 'vue-i18n'
import { getTeamImage, getLeagueImage, getUserAvatarUrl, getUserCoverUrl } from '@/utils/imageUtils'
import { getAvatarColor } from '@/utils/userUtils'
import MatchesTable from '@/components/Tables/MatchesTable.vue'
import TrainingsTable from '@/components/Tables/TrainingsTable.vue'
import NoShowTable from '@/components/Tables/NoShowTable.vue'
import {
  type StatsView,
  type StatsBlock,
  buildStatsView,
  buildBlocks,
  usePlayerStats,
  keyFor,
  valueWithFormat,
} from '@/composables/usePlayerStats'

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:3000'
const isDev = import.meta.env.VITE_STAGE === 'dev'
const UPLOADS = isDev ? `${API_BASE_URL}/uploads` : (import.meta.env.VITE_UPLOADS || `${API_BASE_URL}/uploads`)

const props = withDefaults(defineProps<{
  user: any
  seasonYear: string
  seasonBlocks: StatsBlock[]
  careerBlocks: StatsBlock[]
  allSeasonStats?: any[]
  leagueId?: string | null
  showTables?: boolean
  showShareBadge?: boolean
}>(), {
  allSeasonStats: () => [],
  showTables: true,
  showShareBadge: false,
})

defineEmits<{
  goToTeam: []
  share: []
}>()

/* ── Computed ── */
const fullName = computed(() => {
  const u = props.user
  if (!u) return '-'
  const f = u.firstName ?? ''
  const l = u.lastName ?? ''
  return (f + ' ' + l).trim() || u.name || '-'
})

const initials = computed(() => {
  const u = props.user
  if (!u) return '??'
  if (u.firstName || u.lastName) {
    return `${u.firstName?.[0] ?? ''}${u.lastName?.[0] ?? ''}`.toUpperCase()
  }
  if (u.name) {
    const parts = u.name.split(' ')
    if (parts.length >= 2) return `${parts[0][0]}${parts[1][0]}`.toUpperCase()
    return u.name.substring(0, 2).toUpperCase()
  }
  return '??'
})

const coverImage = computed(() => getUserCoverUrl(props.user) || '')

const avatarImage = computed(() => getUserAvatarUrl(props.user) || '')

const teamLogo = computed(() => {
  return getTeamImage(props.user?._teams?.[0]?.imageUrl || '')
})

const teamName = computed(() => {
  return props.user?._teams?.[0]?.name || ''
})

const { t } = useI18n({ useScope: 'global' })

const seasonView = computed<StatsView>(() => buildStatsView(props.seasonBlocks))
const careerView = computed<StatsView>(() => buildStatsView(props.careerBlocks))
const { labelFor } = usePlayerStats()

/* ── Table refs ── */
const matchesTableRef = ref<InstanceType<typeof MatchesTable> | null>(null)
const trainingsTableRef = ref<InstanceType<typeof TrainingsTable> | null>(null)
const noShowTableRef = ref<InstanceType<typeof NoShowTable> | null>(null)

/** Aggregate raw stats.total objects across multiple phase docs */
function aggregateRawTotals(totals: any[]): any {
  const result: any = { common: {}, skater: {}, goalie: {} }
  const sumField = (path: string[], key: string) =>
    totals.reduce((s, t) => s + (path.reduce((o, k) => o?.[k], t)?.[key] ?? 0), 0)

  for (const k of ['gamesPlayed', 'gamePlayedAsSkater', 'gamePlayedAsGoalie', 'totalWins', 'totalLosses', 'penaltySeconds', 'goalsFor', 'goalsAgainst', 'saves', 'shotsAgainst', 'shotsOnGoal']) {
    result.common[k] = sumField(['common'], k)
  }
  for (const k of ['goals', 'assists', 'points', 'shotsOnGoal', 'penaltySeconds', 'shootoutAttempts', 'shootoutGoals', 'powerPlayGoals']) {
    result.skater[k] = sumField(['skater'], k)
  }
  for (const k of ['shotsAgainst', 'saves', 'goalsAgainst', 'wins', 'losses', 'shutouts', 'shootoutAttempts', 'penaltySeconds']) {
    result.goalie[k] = sumField(['goalie'], k)
  }
  const sa = result.goalie.shotsAgainst
  result.goalie.savePct = sa > 0 ? result.goalie.saves / sa : 0
  const goalieGP = result.common.gamePlayedAsGoalie
  result.goalie.goalsAgainstAverage = goalieGP > 0 ? result.goalie.goalsAgainst / goalieGP : 0
  return result
}

interface SeasonGroup {
  key: string
  leagueLogo: string
  leagueName: string
  seasonYear: string
  totalView: ReturnType<typeof buildStatsView>
  phases: Array<{ phase: string; view: ReturnType<typeof buildStatsView> }>
  hasMultiplePhases: boolean
}

const expandedSeasons = ref<Set<string>>(new Set())

function toggleSeason(key: string) {
  if (expandedSeasons.value.has(key)) {
    expandedSeasons.value.delete(key)
  } else {
    expandedSeasons.value.add(key)
  }
  expandedSeasons.value = new Set(expandedSeasons.value)
}

const PHASE_ORDER: Record<string, number> = { regular: 0, playoff: 1, consolation: 2 }

function phaseLabel(phase: string): string {
  const map: Record<string, string> = {
    regular: t('phaseRegular'),
    playoff: t('phasePlayoff'),
    consolation: t('phaseConsolation'),
  }
  return map[phase] || phase
}

/** Grouped season stats: one entry per league×season, with aggregated total + per-phase breakdown */
const groupedSeasonStats = computed<SeasonGroup[]>(() => {
  const groups = new Map<string, { docs: any[]; leagueLogo: string; leagueName: string; seasonYear: string }>()

  for (const s of props.allSeasonStats) {
    const lid = String((s.leagueId as any)?._id || s.leagueId || '')
    const sid = String((s.seasonId as any)?._id || s.seasonId || '')
    const key = `${lid}_${sid}`
    if (!groups.has(key)) {
      groups.set(key, {
        docs: [],
        leagueLogo: (s.leagueId as any)?.imageUrl || '',
        leagueName: (s.leagueId as any)?.shortName || (s.leagueId as any)?.name || '',
        seasonYear: (s.seasonId as any)?.year || '',
      })
    }
    groups.get(key)!.docs.push(s)
  }

  return Array.from(groups.entries())
    .map(([key, g]) => {
      const totals = g.docs.map(d => d.stats?.total).filter(Boolean)
      const agg = aggregateRawTotals(totals)
      const totalView = buildStatsView(buildBlocks(agg))
      if (!totalView.primary || !totalView.primary.data.gp) return null

      // Merge groups + playoff into single "playoff" phase
      const mergedPhases = new Map<string, any[]>()
      for (const d of g.docs) {
        const raw = d.phase || 'regular'
        const merged = (raw === 'groups' || raw === 'playoff') ? 'playoff' : raw
        if (!mergedPhases.has(merged)) mergedPhases.set(merged, [])
        mergedPhases.get(merged)!.push(d.stats?.total)
      }

      const phases: SeasonGroup['phases'] = []
      for (const [phase, phTotals] of mergedPhases) {
        const valid = phTotals.filter(Boolean)
        if (!valid.length) continue
        const phAgg = valid.length === 1 ? valid[0] : aggregateRawTotals(valid)
        const view = buildStatsView(buildBlocks(phAgg))
        if (!view.primary || !view.primary.data.gp) continue
        phases.push({ phase, view })
      }

      phases.sort((a, b) => (PHASE_ORDER[a.phase] ?? 99) - (PHASE_ORDER[b.phase] ?? 99))

      return {
        key,
        leagueLogo: g.leagueLogo,
        leagueName: g.leagueName,
        seasonYear: g.seasonYear,
        totalView,
        phases,
        hasMultiplePhases: phases.length > 1,
      }
    })
    .filter(Boolean) as SeasonGroup[]
})

/** Role used for the stats table header labels */
const statsHeaderRole = computed(() => {
  if (groupedSeasonStats.value.length) return groupedSeasonStats.value[0].totalView.primary.role
  if (seasonView.value.primary) return seasonView.value.primary.role
  if (careerView.value.primary) return careerView.value.primary.role
  return 'skater' as const
})
</script>

<style lang="scss" scoped>
.pp-body {
  padding: 0;
}

/* ─── HERO ─── */
.hero {
  position: relative;
  width: 100%;
  height: 360px;
  overflow: hidden;
}

.hero-default-bg {
  position: absolute;
  inset: 0;
  background:
    radial-gradient(circle at 20% 30%, rgba(var(--v-theme-primary), 0.35) 0%, transparent 50%),
    radial-gradient(circle at 80% 70%, rgba(var(--v-theme-primary), 0.2) 0%, transparent 50%),
    linear-gradient(135deg,
      rgba(var(--v-theme-sheetBackground), 1) 0%,
      rgba(var(--v-theme-primary), 0.08) 50%,
      rgba(var(--v-theme-sheetBackground), 0.95) 100%
    );
}

.hero-photo {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
}

.hero-photo :deep(.v-img__img) {
  object-position: center 20%;
}

.hero-skeleton {
  position: absolute;
  inset: 0;
}

.hero-skeleton :deep(.v-skeleton-loader) {
  height: 100%;
  border-radius: 0;
}

.hero-skeleton :deep(.v-skeleton-loader__image) {
  height: 100%;
}

/* Fade-in transition */
.hero-fade-enter-active {
  transition: opacity 0.4s ease;
}
.hero-fade-enter-from {
  opacity: 0;
}

.hero-grad {
  position: absolute;
  inset: 0;
  background:
    linear-gradient(
      to top,
      rgb(var(--v-theme-sheetBackground)) 0%,
      rgba(var(--v-theme-sheetBackground), 0.92) 18%,
      rgba(var(--v-theme-sheetBackground), 0.5) 38%,
      rgba(var(--v-theme-sheetBackground), 0.1) 60%,
      transparent 100%
    ),
    linear-gradient(
      to right,
      rgba(var(--v-theme-sheetBackground), 0.6) 0%,
      transparent 50%
    );
}

.hero-identity {
  position: absolute;
  bottom: 88px;
  left: 16px;
  right: 16px;
}

.hero-name {
  font-family: 'Barlow Condensed', sans-serif;
  font-weight: 900;
  font-size: 42px;
  line-height: 1;
  text-transform: uppercase;
  letter-spacing: -1px;
  color: rgb(var(--v-theme-sheetText));
  text-shadow: 0 2px 20px rgba(0, 0, 0, 0.5);
}

.hero-badges {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: 8px;
  flex-wrap: wrap;
}

.badge {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 4px 10px;
  border-radius: 100px;
  font-size: 12px;
  font-weight: 700;
  backdrop-filter: blur(8px);
  -webkit-backdrop-filter: blur(8px);
}

.badge-team {
  background: rgba(var(--v-theme-sheetBackground), 0.65);
  border: 1px solid rgba(var(--v-theme-sheetText), 0.15);
  color: rgb(var(--v-theme-sheetText));
  cursor: pointer;

  img {
    width: 16px;
    height: 16px;
    border-radius: 3px;
    object-fit: contain;
  }
}

.badge-jersey {
  background: rgba(var(--v-theme-sheetBackground), 0.65);
  border: 1px solid rgba(var(--v-theme-sheetText), 0.15);
  color: rgba(var(--v-theme-sheetText), 0.7);
}

.badge-role {
  background: rgba(var(--v-theme-ice), 0.15);
  border: 1px solid rgba(var(--v-theme-ice), 0.25);
  color: rgb(var(--v-theme-ice));
}

.badge-rating {
  background: rgba(var(--v-theme-success), 0.15);
  border: 1px solid rgba(var(--v-theme-success), 0.25);
  color: rgb(var(--v-theme-success));
  font-family: 'Barlow Condensed', sans-serif;
  font-weight: 900;
  font-size: 14px;
}

.badge-share {
  background: rgba(var(--v-theme-sheetBackground), 0.65);
  border: 1px solid rgba(var(--v-theme-sheetText), 0.15);
  color: rgb(var(--v-theme-sheetText));
  cursor: pointer;
  padding: 4px 8px;
}

/* ─── INFO BAR ─── */
.hero-info-bar {
  background: rgb(var(--v-theme-sheetBackground));
  padding: 0 16px 20px;
  display: flex;
  align-items: flex-start;
  gap: 14px;
}

.avatar-wrap {
  flex-shrink: 0;
  margin-top: -44px;
  position: relative;
  z-index: 2;
}

.avatar {
  border: 3px solid rgb(var(--v-theme-sheetBackground)) !important;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.5);
}

.avatar-fallback {
  width: 100%;
  height: 100%;
  display: grid;
  place-items: center;
  color: #fff;
  font-weight: 800;
  font-size: 28px;
  border-radius: 50%;
}

.bio-facts {
  display: flex;
  flex-wrap: wrap;
  gap: 12px 20px;
}

.bio-facts--compact {
  flex: 1;
  min-width: 0;
  padding-top: 8px;
}

.bio-facts--detail {
  background: rgb(var(--v-theme-sheetBackground));
  padding: 0 16px 16px;
}

.fact {
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
}

.fact--wide {
  flex-basis: 100%;
}

.fact-label {
  font-size: 10px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 1.5px;
  color: rgba(var(--v-theme-sheetText), 0.35);
}

.fact-value {
  font-size: 14px;
  font-weight: 600;
  color: rgb(var(--v-theme-sheetText));

  &.empty {
    color: rgba(var(--v-theme-sheetText), 0.28);
  }
}

.fact-value--truncate {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

/* ─── RESPONSIVE ─── */
@media (min-width: 960px) {
  .hero {
    height: 480px;
  }

  .hero-name {
    font-size: 48px;
  }

  .hero-identity {
    bottom: 88px;
    left: 24px;
    right: 24px;
  }

  .hero-info-bar {
    padding: 0 24px 20px;
    gap: 24px;
  }

  .bio-facts {
    gap: 16px 28px;
  }

  .bio-facts--detail {
    padding: 0 24px 16px;
  }

}

/* ─── TABLES SECTION ─── */
.tables-section {
  padding: 16px 0;
  background: rgb(var(--v-theme-sheetBackground));
}

.tables-section :deep(.section-content) {
  background: rgb(var(--v-theme-sheetSection));
  box-shadow: inset 0 0 0 1px rgba(var(--v-theme-sheetText), 0.07);
}

.ios-section {
  padding: 0 16px;
  margin-bottom: 16px;
}

.season-cell {
  display: flex;
  align-items: center;
  gap: 6px;
  white-space: nowrap;
}

.league-logo {
  width: 20px;
  height: 20px;
  border-radius: 50%;
  object-fit: cover;
  flex-shrink: 0;
}

.career-row {
  font-weight: 600;
}

.td-season {
  white-space: nowrap;
}

.expandable-row {
  cursor: pointer;
}
.expandable-row:hover {
  background: rgba(255, 255, 255, 0.03);
}
.expanded-row {
  font-weight: 600;
}

.expand-icon {
  transition: transform 0.2s ease;
  opacity: 0.5;
  margin-right: 2px;
}
.expand-icon--open {
  transform: rotate(90deg);
  opacity: 0.8;
}

.phase-row {
  opacity: 0.7;
  font-size: 0.92em;
}
.phase-row .td-phase {
  padding-left: 36px;
}
.phase-label {
  font-style: italic;
  white-space: nowrap;
}

.load-more-btn {
  width: 100%;
  padding: 10px;
  border-radius: 10px;
  font-size: 13px;
  font-weight: 600;
  color: rgb(var(--v-theme-ice));
  background: rgba(var(--v-theme-ice), 0.08);
  border: none;
  cursor: pointer;
  transition: background 0.2s;
  margin-bottom: 8px;

  &:hover {
    background: rgba(var(--v-theme-ice), 0.15);
  }

  &:disabled {
    opacity: 0.5;
    cursor: default;
  }
}

.load-more-count {
  opacity: 0.5;
}
</style>
